<template>
  <div class="accordion">
    <h2 v-if="content.title" class="h2-article">{{ content.title }}</h2>

    <ul class="accordion__items">
      <li
        v-for="(item, idx) in content.items"
        :key="item.title"
        :class="{ 'is-active': active === idx && isOpen }"
        class="accordion__items__item"
      >
        <h3>
          <button
            type="button"
            class="accordion__items__item__trigger h3"
            @click="toggle(idx)"
          >
            {{ item.title }}

            <SvgSprite symbol="ui-full-arrow" size="28" />
          </button>
        </h3>

        <div ref="items" class="accordion__items__item-outer">
          <Wysiwyg
            :content="item"
            class="accordion__items__item__copy"
          ></Wysiwyg>
        </div>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { gsap } from 'gsap'
import { ref } from 'vue'
import Wysiwyg from '../Wysiwyg.vue'

type Content = {
  title?: string
  items: Array<{ title: string; htmltext: string }>
}

defineProps<{ content: Content }>()

const active = ref()
const items = ref<HTMLElement[]>()
const isOpen = ref(false)

const close = (el: HTMLElement) => {
  gsap.to(el, {
    duration: 0.15,
    height: 0,
  })
  isOpen.value = false
}

const open = (el: HTMLElement) => {
  gsap.set(el, { height: 'auto' })
  gsap.from(el, {
    duration: 0.15,
    height: 0,
  })
  isOpen.value = true
}

const toggle = (idx: number) => {
  const prev = items.value?.[active.value]

  if (idx === active.value && prev) {
    if (isOpen.value) {
      close(prev)
    } else {
      open(prev)
    }

    return
  }

  if (prev) {
    close(prev)
  }

  active.value = idx
  const current = items.value?.[idx]

  if (current) {
    open(current)
  }
}
</script>

<style lang="scss" scoped>
.accordion {
  display: flex;
  flex-direction: column;
  gap: 4rem;
}

.accordion__items {
  margin: 0;
  padding: 0;
}

.accordion__items__item {
  list-style-type: none;
  border-top: 1px solid var(--c-foreground);
}

.accordion__items__item__trigger {
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 2rem 0;
  color: var(--c-foreground);
  font-weight: 600;
  text-align: left;
  background: transparent;
  border: 0;

  :deep(svg) {
    fill: var(--c-foreground);
    transform: rotate(90deg);
    transition: transform 0.15s;
  }

  .is-active & {
    :deep(svg) {
      transform: rotate(90deg) scale(-1);
    }
  }
}

.accordion__items__item-outer {
  overflow: hidden;
  height: 0;

  .is-active & {
    height: auto;
  }
}

.accordion__items__item__copy {
  max-width: 76rem;
  padding: 0 0 2rem;
  color: var(--c-foreground);
  font-weight: 400;
  line-height: 2.5rem;

  &.wysiwyg {
    width: 100%;
    margin: 0;
  }
}
</style>
